import React, { useEffect } from 'react';
import {
    TrendingDown, Plus, GripVertical, X, FileText, Loader2, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SearchBar } from '@/components/SearchBox/SearchBar';
import { useLanguage } from '../../../../context/LanguageContext';
import { SearchBarPagination } from '@/components/SearchBox/SearchBarPagination';
import { ENTITY_SUGGESTION_TYPES, getEntitySuggestionSearchAPI } from '@/redux/Common/actions';

// Factory function to create empty AP charge row

export const createEmptyAPChargeRow = () => ({
    rowid: crypto.randomUUID(), // Unique row ID for React key
    ID: 0,
    CHARGE_CODE: '',
    DESCRIPTION: '',
    CURRENCY: 'USD',
    ROE: '1.000000',
    BASIS: '',
    CONTAINER: '',
    QUANTITY: '1',
    VENDOR: '',
    VENDOR_ID: 0,
    VENDOR_REF: '',
    AMOUNT: '',
    BUY_RATE: '',
    PPCC: 'P',
    BILL_STATUS: 'not_billed',
    BILL_ID: null,
    BILL_NO: '',
    IS_ACTIVE: true,
});

// Sortable row component
const SortableRow = ({ charge, index, children }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: charge.rowid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`border-b border-green-100 dark:border-green-900/30 hover:bg-green-50/50 dark:hover:bg-green-900/10 transition-colors ${isDragging ? 'bg-green-100 dark:bg-green-900/30' : ''
                }`}
        >
            <td className="px-2 py-2 w-8">
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-green-100 dark:hover:bg-green-800 rounded"
                    title="Drag to reorder"
                >
                    <GripVertical className="w-4 h-4 text-slate-400" />
                </button>
            </td>
            {children}
        </tr>
    );
};

const APChargesTable = ({
    charges,
    setCharges,
    currencyList = [],
    basisList = [],
    containerList = [],
    chargeCodeList = [],
    onSyncToBill,
    isSyncing = false,
    bookingId,
    totalbill
}) => {
    const { t } = useLanguage();
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    // Backfill `rowid` whenever any row is missing one (e.g. after API load
    // or when the parent replaces the array). Without this, all rows would
    // share `rowid: undefined`, causing edits/deletes to apply to every row.
    useEffect(() => {
        if (!charges || charges.length === 0) return;
        const needsUpdate = charges.some((c) => !c?.rowid);
        if (!needsUpdate) return;
        setCharges(charges.map((c) => ({
            ...c,
            rowid: c?.rowid || (typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `row-${Date.now()}-${Math.random().toString(36).slice(2)}`),
        })));
    }, [charges, setCharges]);


    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = charges.findIndex((c) => c.rowid === active.id);
            const newIndex = charges.findIndex((c) => c.rowid === over.id);
            setCharges(arrayMove(charges, oldIndex, newIndex));
        }

    };

    const addCharge = () => {
        setCharges([...charges, createEmptyAPChargeRow()]);
    };

    const removeCharge = (rowid) => {
        if (!rowid) return;
        setCharges(charges.filter((c) => c.rowid !== rowid));
    };

    const updateCharge = (rowid, field, value) => {
        if (!rowid) return;
        setCharges(prevCharges => {
            const updatedCharges = prevCharges.map((c) => {
                if (c.rowid && c.rowid === rowid) {
                    const updated = { ...c, [field]: value };
                    if (field === 'AMOUNT' || field === 'QUANTITY' || field === 'ROE') {
                        const amount = parseFloat(updated.AMOUNT) || 0;
                        const qty = parseFloat(updated.QUANTITY) || 1;
                        const roe = parseFloat(updated.ROE) || 1;
                        updated.BUY_RATE = ((amount * qty) / roe).toFixed(2);
                    }
                    return updated;
                }
                return c;
            });
            return updatedCharges;
        });
    };

    const getStatusBadge = (charge) => {
        const status = charge.BILL_STATUS;
        const billNo = charge.BILL_NO;

        switch (status) {
            case 'billed':
            case 'approved':
            case 'paid':
                return (
                    <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                            {billNo || 'Billed'}
                        </span>
                        {billNo && (
                            <a
                                href={`/accounting/bills`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-0.5 hover:bg-green-100 rounded"
                                title="View Bill"
                            >
                                <ExternalLink className="w-3 h-3 text-green-600" />
                            </a>
                        )}
                    </div>
                );
            case 'draft':
                return (
                    <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded">
                            {billNo || 'Draft'}
                        </span>
                        {billNo && (
                            <a
                                href={`/accounting/bills`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-0.5 hover:bg-blue-100 rounded"
                                title="View Bill"
                            >
                                <ExternalLink className="w-3 h-3 text-blue-600" />
                            </a>
                        )}
                    </div>
                );
            case 'not_billed':
            default:
                return <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded block">{t('notBilled')}</span>;
        }
    };

    const totalAP = charges.reduce((sum, c) => {
        const amount = parseFloat(c.AMOUNT) || 0;
        const qty = parseFloat(c.QUANTITY) || 1;
        return sum + (amount * qty);
    }, 0);

    const inputClass = "w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 focus:ring-1 focus:ring-green-500 focus:border-green-500";
    const selectClass = "w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 focus:ring-1 focus:ring-green-500";

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-green-500" />
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        {t('accountsPayable')} ({t('costs')})
                    </h3>
                </div>
                <div className="flex items-center gap-2">
                    {onSyncToBill && bookingId && bookingId !== 0 && charges.length && totalbill > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onSyncToBill}
                            disabled={isSyncing}
                            className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                            data-testid="sync-ap-to-bill-btn"
                        >
                            {isSyncing ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                                <FileText className="w-3 h-3 mr-1" />
                            )}
                            {t('syncToBill')}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={addCharge}
                        className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                        data-testid="add-ap-charge-btn"
                    >
                        <Plus className="w-3 h-3 mr-1" /> {t('addAPCharge')}
                    </Button>
                </div>
            </div>

            {/* Table */}
            <div className="border border-green-200 dark:border-green-800 rounded-lg overflow-auto">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-green-500 text-white">
                                <th className="px-2 py-2 w-8"></th>
                                <th className="px-2 py-2 text-left font-medium w-12">SNO</th>
                                <th className="px-2 py-2 text-left font-medium min-w-[180px]">CHARGE DESCRIPTION</th>
                                <th className="px-2 py-2 text-center font-medium w-16">CURR</th>
                                <th className="px-2 py-2 text-center font-medium w-20">ROE</th>
                                <th className="px-2 py-2 text-center font-medium w-20">BASIS</th>
                                <th className="px-2 py-2 text-center font-medium w-20">CONTAINER</th>
                                <th className="px-2 py-2 text-center font-medium w-14">QTY</th>
                                <th className="px-2 py-2 text-left font-medium min-w-[140px]">VENDOR</th>
                                <th className="px-2 py-2 text-left font-medium w-20">REF #</th>
                                <th className="px-2 py-2 text-right font-medium w-24">AMOUNT</th>
                                <th className="px-2 py-2 text-right font-medium w-24">BUY RATE</th>
                                <th className="px-2 py-2 text-center font-medium w-16">PP/CC</th>
                                <th className="px-2 py-2 text-center font-medium w-20">STATUS</th>
                                <th className="px-2 py-2 text-center font-medium w-10">DEL</th>
                            </tr>
                        </thead>

                        <SortableContext
                            items={charges.map((c) => c.rowid)}
                            strategy={verticalListSortingStrategy}
                        >
                            <tbody>
                                {charges.length === 0 ? (
                                    <tr>
                                        <td colSpan={16} className="px-4 py-8 text-center text-slate-500">
                                            {t('noAPCharges')}
                                        </td>
                                    </tr>
                                ) : (
                                    charges.map((charge, index) => (
                                        <SortableRow key={charge.rowid || `ap-row-${index}`} charge={charge} index={index}>
                                            <td className="px-2 py-2 text-center font-medium text-slate-600">
                                                {index + 1}
                                            </td>
                                            <td className="px-1 py-1 min-w-[180px]">
                                                <SearchBarPagination
                                                    searchAPI={'v1/Common/ChargesDescription_List?flag=0&searchKeyword='}
                                                    defaultSearch="o"
                                                    selectReacord={(d) => {
                                                        updateCharge(charge.rowid, 'CHARGE_CODE', d?.[0]?.toString() || '');
                                                        updateCharge(charge.rowid, 'DESCRIPTION', d?.[1] || '');
                                                    }}

                                                    value={charge.DESCRIPTION || ''}
                                                    onchange={(val) => {
                                                        if (!val) {
                                                            updateCharge(charge.rowid, 'CHARGE_CODE', '');
                                                            updateCharge(charge.rowid, 'DESCRIPTION', '');
                                                        }
                                                    }}
                                                    renderItem={(item) => item.chargesdescription}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.CURRENCY}
                                                    onChange={(e) => updateCharge(charge.rowid, 'CURRENCY', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="USD">USD</option>
                                                    {currencyList?.map((c, i) => (
                                                        <option key={i} value={c.currencycode}>{c.currencycode}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number"
                                                    step="0.000001"
                                                    value={charge.ROE}
                                                    onChange={(e) => updateCharge(charge.rowid, 'ROE', e.target.value)}
                                                    className={`${inputClass} text-center`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.BASIS}
                                                    onChange={(e) => updateCharge(charge.rowid, 'BASIS', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="">Select</option>

                                                    {basisList?.map((b, i) => (
                                                        <option key={i} value={b.chargebasis}>{b.chargebasis}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.CONTAINER}
                                                    onChange={(e) => updateCharge(charge.rowid, 'CONTAINER', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="">Select</option>

                                                    {containerList?.map((c, i) => (
                                                        <option key={i} value={c.moveType}>{c.moveType}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={charge.QUANTITY}
                                                    onChange={(e) => updateCharge(charge.rowid, 'QUANTITY', e.target.value)}
                                                    className={`${inputClass} text-center w-14`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <div className="flex items-center gap-1">
                                                    <div className="flex-1">
                                                        <SearchBarPagination
                                                            searchAPI={getEntitySuggestionSearchAPI(ENTITY_SUGGESTION_TYPES.VENDOR)}
                                                            defaultSearch="m"
                                                            selectReacord={(d) => {
                                                                updateCharge(charge.rowid, 'VENDOR', d?.[1]);
                                                                updateCharge(charge.rowid, 'VENDOR_ID', d?.[0]);
                                                            }}
                                                            value={charge.VENDOR || ''}
                                                            onchange={(val) => {
                                                                if (!val) {
                                                                    updateCharge(charge.rowid, 'VENDOR', '');
                                                                    updateCharge(charge.rowid, 'VENDOR_ID', 0);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {charge.VENDOR && (
                                                        <button
                                                            onClick={() => {
                                                                updateCharge(charge.rowid, 'VENDOR', '');
                                                                updateCharge(charge.rowid, 'VENDOR_ID', 0);
                                                            }}
                                                            className="p-1 hover:bg-red-50 rounded text-red-500"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="text"
                                                    value={charge.VENDOR_REF || ''}
                                                    onChange={(e) => updateCharge(charge.rowid, 'VENDOR_REF', e.target.value)}
                                                    className={inputClass}
                                                    placeholder="Ref #"
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={charge.AMOUNT}
                                                    onChange={(e) => updateCharge(charge.rowid, 'AMOUNT', e.target.value)}
                                                    className={`${inputClass} text-right`}
                                                    placeholder="0.00"
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="text"
                                                    value={charge.BUY_RATE || '0.00'}
                                                    readOnly
                                                    className={`${inputClass} text-right bg-slate-50 dark:bg-slate-900`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <select
                                                    value={charge.PPCC}
                                                    onChange={(e) => updateCharge(charge.rowid, 'PPCC', e.target.value)}
                                                    className={selectClass}
                                                >
                                                    <option value="P">P</option>
                                                    <option value="C">C</option>
                                                </select>
                                            </td>
                                            <td className="px-2 py-1 text-center">

                                                {getStatusBadge(charge)}

                                            </td>
                                            <td className="px-2 py-1 text-center">
                                                <button
                                                    onClick={() => removeCharge(charge.rowid)}
                                                    className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded text-red-500"
                                                    title="Delete charge"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </SortableRow>
                                    ))
                                )}
                            </tbody>
                        </SortableContext>

                    </table>
                </DndContext>
            </div>

            {/* Total */}
            <div className="flex justify-end">
                <div className="px-4 py-2 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-lg">
                    <span className="text-sm font-semibold text-green-800 dark:text-green-200" data-testid="total-ap-amount">
                        {t('totalAP')} (USD): ${totalAP.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default APChargesTable;
